import classNames from "classnames";
import { useContext, useState } from "react";
import { BrandliveEvent, LanguagesAbbr } from "types/working-model";
import Icon, { ICONS } from '@general-ui/icon';
import { useTranslate } from "i18n/useTranslationModules";
import { CurrentPageState } from "./navbar.hooks";
import AppContext, { AppContexts } from "components/app-context";
import useChangeLanguage from "utils/use-change-language";
import { useDispatch } from "react-redux";
import store from "store/main";
import { showAlert } from "@general-ui/alert/alert-service";
import { blurEditor, loadWorkingEvent } from "store/actions/admin/create-event";
import { AnyAction, Dispatch } from "redux";
import { setEventNavOpen } from "store/actions/event/event-actions";
import { handleButtonKeyDown } from "utils/keyboard-activation";

const updateAdminTranslations = (dispatch: Dispatch<AnyAction>, uuid: string) => {
	const token = store.getState().AuthReducer.token;

	if (!token) return;

	showAlert({
		message: "Fetching translations",
		description: "Please wait a moment while we get new translations",
		duration: 4000,
		type: 'positive'
	});
	dispatch(blurEditor(true));
	dispatch(loadWorkingEvent(uuid, token));
};

type Props = {
	language: LanguagesAbbr;
	languages: LanguagesAbbr[];
	pageState: CurrentPageState;
	eventBundle: BrandliveEvent;
}
const NavbarLanguageSelector: React.FC<Props> = ({ eventBundle, language, languages, pageState }) => {
	const appContext = useContext(AppContext);
	const isAdmin = appContext === AppContexts.admin;
	const [open, setOpen] = useState(false);
	const { t } = useTranslate('languages-list');
	const dispatch = useDispatch();
	const handleLanguageChange = useChangeLanguage(eventBundle);

	const changeLanguage = (lang: LanguagesAbbr) => {
		if (isAdmin) {
			updateAdminTranslations(dispatch, eventBundle.uuid);
		}

		handleLanguageChange(lang);
		dispatch(setEventNavOpen(false));
	};

	return (
		<div
			className={classNames(
				"navbar__language-selector",
				{
					"navbar__language-selector-dropdown-open": open
				}
			)}
		>
			<div
				className="navbar__language-selector-current"
				onClick={() => setOpen(!open)}
				onKeyDown={handleButtonKeyDown}
				role="button"
				tabIndex={0}
				aria-expanded={open}
			>
				<div className="navbar__language-selector-current-label">
					<Icon name={ICONS.TRANSLATIONS} color={''} size={24} />
					<span>{t(`Languages.${language}`, language)} ({language})</span>
				</div>
				<Icon name={ICONS.ANGLE_RIGHT} color={''} size={16} />
			</div>
			<div
				className={
					classNames(
						"navbar__language-selector-dropdown"
					)
				}
			>
				<div className="navbar__language-selector-dropdown-inner">
					{
						languages.filter(lang => lang !== language).map((lang, i) => (
							<div
								key={i}
								className="navbar__language-selector-dropdown-item"
								onClick={() => changeLanguage(lang)}
								onKeyDown={handleButtonKeyDown}
								role="button"
								tabIndex={0}
							>
								<span>{t(`Languages.${lang}`)} ({lang})</span>
							</div>
						))
					}
				</div>
			</div>
		</div>
	);
};

export default NavbarLanguageSelector;
